/**
 * Expected Checks → Records → Findings (docs/PRINCIPLES.md G3–G6, S2–S4).
 * Pure functions: the API, web and mobile all compute the same Findings from the same data.
 */
import { lessonsGateDetail, onboardingStatus, type OnboardingInput } from "./onboarding.ts";
import type { Clearance, DailyCheckin, Employee, Id, Incident, IsoDate, IsoDateTime, LogEntry, PermitType, ReviewItem, TaskDefinition } from "./model.ts";
import { getTemplate } from "./templates.ts";
import { sanitizerCheckText, type SanitizerSetup } from "./sanitizer.ts";
import { howToFor, type HowTo } from "./howto.ts";
import { checkinAction, checkinActionsFor } from "./checkin.ts";
import { HEALTH_DECISION_BLOCK_TITLE, decisionInEffect, healthDecisionLine, latestDecision, type HealthDecision } from "./returntowork.ts";
import { firstName, personActions, supplyActions, type QuickAction, type Supply } from "./actions.ts";
import { PEST_QUICK_LOOK_LABEL, PEST_WALKTHROUGH_LABEL, PEST_TEMPLATE_ID, isLegacyPestWalkthrough } from "./pest.ts";
import { counts } from "./corrections.ts";
import { addLocalDays, hoursOn, isOvernight, isoAtMinute, spanMinutes, toMinutes, type WeeklyHours } from "./hours.ts";
import { coolingBatch, coolingCheckDone, coolingChecks, coolingNotices, isOpenBatch } from "./cooling.ts";
import { fixStatusOf, recheckKey } from "./whatidid.ts";
import { seedForTask, taskFitsMenu } from "./tasks.ts";
import { checkLawsAt, picCertNote, shapesAt, type CheckLaw } from "./ruleshapes.ts";
import { certDay, courseLine } from "./certscan.ts";

/** Days before a course certificate's permit-by day that the manager is reminded (fix/cert-scan). */
// The same day as the worker's reminder (certscan.ts TEMP_CERT_REMIND_DAYS; a literal here: the modules import each other).
export const COURSE_REMIND_DAYS = 7;
import { SCHEDULE_UT } from "./states/ut/schedule.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof SCHEDULE_UT>> = perStateTable("SCHEDULE", SCHEDULE_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

// The check rules, levels and cadence words live in cadence.ts (a leaf the sheets and the site copy read too); re-exported here.
import { BASE_CHECKS, CALIBRATE_DOUBT_HINT, CALIBRATE_DOUBT_KEY, LEVEL_RANK, LOG_LEVELS, timingOf, type CadenceOverrides, type CheckRule, type LegalCadence, type LogLevel } from "./cadence.ts";
import type { PhotoAsk, ProofSettings } from "./proof.ts";
export { BASE_CHECKS, defaultTiming, timingOf, type CadenceOverrides, type CheckTiming, CALIBRATE_DOUBT_HINT, CALIBRATE_DOUBT_KEY, CALIBRATE_EVERY_DAYS, CALIBRATE_HINT, CALIBRATE_KEY, CALIBRATE_LABEL, LEVEL_PARAM, LEVEL_RANK, LOG_LEVELS, WANT_MORE, levelFromParam, levelFromQuery, cadenceLine, cadenceOf, cadenceWords, type Cadence, type CheckRule, type LegalCadence, type LogLevel, type Trigger } from "./cadence.ts";

/** Logs whose failing reading casts doubt on the thermometer: an extra ice-water check follows the same day (calibrate-doubt). */
const DOUBT_TEMPLATES = new Set(["cold_units", "temp_cook_hold", "tcs_items", "receiving", "transport_temp", "cooling"]);

/** The last day a thermometer check was recorded (the weekly "open-calibrate" check is due CALIBRATE_EVERY_DAYS after it). */
export function lastCalibration(entries: readonly Pick<LogEntry, "templateId" | "date">[]): IsoDate | undefined {
  let last: IsoDate | undefined;
  for (const e of entries) if (counts(e as Pick<LogEntry, "templateId" | "date"> & { correction?: unknown }) && e.templateId === "calibration" && (!last || e.date > last)) last = e.date;
  return last;
}

/**
 * The law's cadences at a place as template cadences (cadence.ts LegalCadence), for the sheet and screen words: a check the law
 * names at opening or closing, or every N hours (ruleshapes.ts checkLawsAt; never looser than the rule's own, lawInterval).
 */
export function legalCadences(place: { state?: string | null; county?: string | null }, permit: PermitType, overrides?: CadenceOverrides): LegalCadence[] {
  const laws = checkLawsAt(place, permit);
  const out: LegalCadence[] = [];
  for (const r of BASE_CHECKS) {
    const l = laws[r.key];
    if (l?.status !== "law") continue;
    if (r.trigger === "shift_open") out.push({ templateId: r.templateId, atOpen: true });
    else if (r.trigger === "shift_close") out.push({ templateId: r.templateId, atClose: true });
    // The place's own timing, capped by the law's (the same numbers expectedChecks runs).
    else if (r.trigger === "every_hours") out.push({ templateId: r.templateId, everyHours: lawInterval(l, overrides?.[r.key]?.everyHours ?? r.everyHours) });
    else if (r.trigger === "every_days") out.push({ templateId: r.templateId, everyDays: lawDays(l, overrides?.[r.key]?.everyDays ?? r.everyDays) });
  }
  return out;
}

/** The last local day a record of this log was made (the day an every-N-days check counts from); undefined when never. */
export function lastRecordDay(entries: readonly Pick<LogEntry, "templateId" | "date">[], templateId: string): IsoDate | undefined {
  let last: IsoDate | undefined;
  for (const e of entries) if (counts(e as Pick<LogEntry, "templateId" | "date"> & { correction?: unknown }) && e.templateId === templateId && (!last || e.date > last)) last = e.date;
  return last;
}

/**
 * Why the app recommends each built-in check, one line, shown wherever the check is the app's advice rather than the law's
 * (docs/compliance/CHECKS-BY-LAW.md). The three opening checks follow the FDA's foodborne-illness risk factors (official-riskfactors.ts:
 * improper holding, contaminated equipment, poor hygiene). Never the word "required": that is the law's word (checkCopyProblems).
 */
export const CHECK_WHY: Readonly<Record<string, string>> = {
  "open-calibrate": "Every temperature below depends on a thermometer that reads true. The ice-water check takes a minute, once a week.",
  "open-cold": "Cold food at 41°F or less is the inspection item most tied to people getting sick. One reading at opening catches a fridge that failed overnight.",
  "open-sanitizer": "Sanitizer at the right strength is how clean dishes stay clean. A strip test at opening proves the bucket is right.",
  "open-restroom": "Soap, towels and warm water at the sink: no handwashing without them.",
  "cold-4h": "A fridge that fails mid-shift is caught the same shift, not at close.",
  "sanitizer-4h": "Sanitizer weakens as the bucket is used; a retest mid-shift keeps the cloths safe.",
  "tcs-2h": "Each item's temperature every 2 hours is how a home kitchen shows its food stayed cold.",
  "commissary-visit": "A written visit log is how an inspector sees the truck really returns to its commissary.",
  "restroom-4h": "An empty soap pump mid-shift means no handwashing until someone notices.",
  "close-cold": "A closing reading brackets the day: the fridge held from open to close.",
  "open-pest-look": "A 30-second look at the floor, the corners and the deliveries is how pests are caught early.",
  "close-pest-look": "A 30-second look at the floor, the corners and the deliveries is how pests are caught early.",
  "cooling": "The 2-hour and 6-hour readings prove each batch met the cooling limits.",
  "pest-walkthrough": "Pests show up in corners and behind equipment first; a weekly walk finds them before an inspector does.",
  // Never shown: the route sheet runs only where the law names it (cadence.ts lawOnly), so its row always carries the law's line.
  "route-sheet": "A health district that asks for a monthly route sheet wants it before the month starts; this is the reminder to send it.",
};

/** Words the app never uses about a check (owner, 2026-10-11): a recommendation is never "required", and no safety claim is dressed up. */
const NEVER_WORDS: RegExp[] = [/\brequired\b/i, /\bofficial\b/i, /stricter than the health department/i, /pass your inspection/i];

/**
 * Every recommendation line and level summary, checked against NEVER_WORDS: empty when the copy is clean. A test runs it on the
 * real tables (and on a bad line, as the control). Pass `extra` lines to lint words built at runtime.
 */
export function checkCopyProblems(extra: readonly string[] = []): string[] {
  const out: string[] = [];
  const lines: [string, string][] = [...Object.entries(CHECK_WHY).map(([k, v]) => [`why.${k}`, v] as [string, string]), ...LOG_LEVELS.map((l) => [`level.${l.id}`, l.summary] as [string, string]), ...extra.map((e, i) => [`extra.${i}`, e] as [string, string])];
  for (const [where, text] of lines) for (const re of NEVER_WORDS) if (re.test(text)) out.push(`${where}: "${text}" uses ${re.source}`);
  for (const r of BASE_CHECKS) if (!CHECK_WHY[r.key]) out.push(`why.${r.key}: no recommendation line`);
  return out;
}

/** What a check's row says about why it's there: the law's own line where the law names it, else the app's one-line why. */
export interface CheckBasis {
  /** The law at the place names this record (or daily act) for the permit. */
  required: boolean;
  /** The line: the law's plain words ("Utah law: …"), or "Recommended. <why>", with "not confirmed … ask them" where the law wasn't read. */
  line: string;
}

/** The basis line for a built-in check at a place (schedule.ts CHECK_WHY, ruleshapes.ts checkLawsAt). */
export function checkBasis(key: string, law: CheckLaw | undefined): CheckBasis {
  if (law?.status === "law") return { required: true, line: law.value.plain };
  const why = CHECK_WHY[key] ?? "";
  const ask = law?.status === "unknown" ? ` Not confirmed as a rule for ${law.confirm?.name ?? "your health department"}: ask them.` : "";
  return { required: false, line: `Recommended. ${why}${ask}`.trim() };
}

/** The basis line of an expected check as the screens show it ("Utah law: …" or "Recommended. …"); undefined for checklist items. */
export const checkBasisLine = (e: Pick<ExpectedCheck, "law" | "why">): string | undefined => e.law ?? e.why;

export interface ExpectedCheck {
  key: string; // stable per day: `${rule.key}@${HH:MM}`
  label: string;
  templateId: string;
  taskId?: Id;
  dueAt: IsoDateTime;
  lateAt: IsoDateTime;
  blocking: boolean;
  /**
   * The law at the place names this record (or daily act) for the permit (states/<st>-shapes.ts `checks`): it runs at every
   * level, at the law's cadence or tighter, and a shortfall escalates to the owner. Never true of a recommendation, blocking or
   * not: "Open for food" is the app's gate, not the law's (docs/compliance/CHECKS-BY-LAW.md).
   */
  required: boolean;
  /** The law's own plain line, when `required` ("Utah law: …"). */
  law?: string;
  /** Why the app recommends it, when it is advice ("Recommended. …", with "ask them" where the law wasn't read). */
  why?: string;
  /** One line on how to do it right (shown under the label). */
  hint?: string;
  /** What you need, step by step how, and why (howto.ts). */
  howTo?: HowTo;
  requirementIds: string[];
  /** Only a Record with these values satisfies it (CheckRule.match). */
  match?: Record<string, string>;
  /**
   * Due before this place's first full operating day (DayContext.startedAt): it's where the owner starts, never
   * "Was due" or late. Absent on every later day.
   */
  firstDay?: true;
  /**
   * An opening check counts from here, not only from an hour before it's due: the day's first check of its kind counts
   * whenever it was made that day (set up at 3:50 AM and calibrate, and the 7:00 AM calibration is done). The day starts
   * at midnight, or when last night's overnight shift closed (hours.ts H3), so last night's closing log never counts.
   * Only on opening checks (shift_open rules and opening checklist items).
   */
  windowFrom?: IsoDateTime;
  /** A cooling batch's reading (core cooling.ts): satisfied only by that batch's own record. */
  cooling?: { entryId: Id; stage: "2h" | "6h"; food: string };
  /**
   * A recheck after "What I did" (core whatidid.ts W4): due at the record's recheck time, answered only by a reading of the
   * same template and the same unit or food (`match`) made after `after`. The usual due/late reminders carry it.
   */
  recheck?: { entryId: Id; after: IsoDateTime };
  /**
   * A check that can take a photo (core proof.ts expectedWithProof): which kind, the place's proof setting, and whether a photo is
   * asked for on this one ("daily": one picked per day; "every": each). Never a wall: the record saves without it.
   */
  photo?: PhotoAsk;
}

export interface DayContext {
  /** The state whose rules apply (absent = Utah, the default). */
  state?: UsState;
  /** The place's county: picks its health authority's rule shapes (ruleshapes.ts). Absent = the state layer only. */
  county?: string | null;
  /** Logging level for this location; defaults to "standard". */
  level?: LogLevel;
  /** The location (seeds the once-a-day photo pick, core proof.ts proofPick). */
  locationId?: Id;
  /** How much photo proof each kind of check asks for (Location.proof). Absent: a photo is offered, never asked. */
  proof?: ProofSettings;
  /** The place's own timing per check (Location.cadence, cadence.ts timingOf), capped by the law. */
  cadence?: CadenceOverrides;
  /** How sanitizing is done here (changes the sanitizer check wording). */
  sanitizer?: SanitizerSetup;
  /** Formats an ISO time as local clock time for this location, e.g. "7:00 AM". Defaults to the UTC HH:MM. */
  timeLabel?: (iso: IsoDateTime) => string;
  date: IsoDate;
  now: IsoDateTime;
  /**
   * When this location (or its business) was set up (Location.createdAt). A day that started before then isn't a full
   * operating day, so nothing on it is late: set up at 5 pm, and the 7 am opening checks are where you start, not
   * "Was due 7:00 AM" (docs/ux/STORIES-OPERATORS.md §0.1). Absent: every day is a full day.
   */
  startedAt?: IsoDateTime;
  permit: PermitType;
  /** Fallback hours "HH:MM" local (Location.openAt/closeAt): used on a day `hours` doesn't give. */
  openAt: string;
  closeAt: string;
  /** Weekly hours (Location.hours, hours.ts). A closed day expects nothing: nothing due, late or missed. */
  hours?: WeeklyHours;
  toIso: (date: IsoDate, hhmm: string) => IsoDateTime; // timezone-aware conversion supplied by caller
  /** `how` (the owner's own words, or the starter seed's) becomes the row's hint and the item card's how (crawl fix 3). */
  tasks: (Pick<TaskDefinition, "id" | "title" | "category" | "schedule" | "requirementIds" | "active"> & Partial<Pick<TaskDefinition, "how">>)[];
  lastDone: Record<Id, IsoDate | undefined>; // taskId → last completion date
  /** Built-in every-N-days checks: rule key → the last day a Record satisfied it ("pest-walkthrough"). Absent: never. */
  lastChecks?: Record<string, IsoDate | undefined>;
  entries: (Pick<LogEntry, "id" | "templateId" | "taskId" | "createdAt" | "evaluation" | "correctiveAction" | "values"> & { fix?: LogEntry["fix"] })[];
  /** Records with a recheck due from any day (core whatidid.ts); today's records are read from `entries` too. */
  rechecks?: (Pick<LogEntry, "id" | "templateId" | "values" | "createdAt"> & { fix?: LogEntry["fix"] })[];
  checkins: (Pick<DailyCheckin, "employeeId" | "okToWork" | "date"> & { health?: DailyCheckin["health"]; at?: IsoDateTime })[];
  /**
   * Check-ins the same people made today at the business's OTHER locations (api day.ts). A daily health check-in is per
   * person per local day (Food Code 2-201.11 reporting is about the person), so it counts here too (`checkinFor`).
   * Kept apart from `checkins` so "check-ins at this place" counts stay this place's. Absent: none.
   */
  carriedCheckins?: DayContext["checkins"];
  onShift: (Pick<Employee, "id" | "name" | "isPersonInCharge" | "phone"> & { onboarding: OnboardingInput })[];
  /** Where supplies are kept and who to call (Location.supplies). */
  supplies?: Supply[];
  /** This place cooks and cools food (core cooling.ts coolsFood): the cooling log is a base check here. */
  cooks?: boolean;
  /** The menu (Location.menuItemIds): a task seeded for one dish (the buffet sign) applies only while it's on the menu (tasks.ts taskFitsMenu). Absent: every task applies. */
  menuItemIds?: string[];
  /**
   * Cooling-log records to time (core cooling.ts): the ones still open, whatever day they started, and today's. They
   * must also be in `entries` (a check is satisfied by its own batch's record).
   */
  cooling?: Pick<LogEntry, "id" | "values" | "createdAt" | "createdBy" | "correctiveAction" | "cellSources" | "correction">[];
  /** Open reports. `type` and `reportedBy` (optional for older callers) let shiftGate see a sick report from someone on shift. */
  incidents: (Pick<Incident, "id" | "title" | "severity" | "status" | "reportedAt" | "requirementIds"> & Partial<Pick<Incident, "type" | "reportedBy">>)[];
  reviews: Pick<ReviewItem, "id" | "status">[];
  /**
   * The person in charge's recorded health decisions at this location (returntowork.ts; the newest per person counts).
   * An exclusion holds until its date, a restriction until cleared, whatever later check-ins say (Food Code 2-201.13).
   */
  healthDecisions?: Pick<HealthDecision, "employeeId" | "decision" | "until" | "condition" | "at">[];
}

/**
 * This person's check-in for `ctx.date`: one made at this location, else one made today at another location of the same
 * business (`carriedCheckins`; its date is that place's local day, so it only counts when it equals this place's day).
 */
export function checkinFor(ctx: Pick<DayContext, "checkins" | "date" | "carriedCheckins">, employeeId: Id): DayContext["checkins"][number] | undefined {
  const here = ctx.checkins.find((c) => c.employeeId === employeeId && c.date === ctx.date);
  return here ?? (ctx.carriedCheckins ?? []).find((c) => c.employeeId === employeeId && c.date === ctx.date);
}

const addMin = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) + m * 60_000).toISOString();
const hhmm = (mins: number) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

/**
 * Is `ctx.date` before this place's first full operating day? True when the place was set up after that day's
 * opening time (or on a later day): the day it was set up is where it starts, and the next day is its first full one.
 */
export function beforeFirstFullDay(ctx: Pick<DayContext, "startedAt" | "date" | "openAt" | "closeAt" | "toIso"> & { hours?: WeeklyHours }): boolean {
  if (!ctx.startedAt) return false;
  const h = hoursOn(ctx, ctx.date);
  // A closed day expects nothing, so it's never a "first day" either.
  if (h === "closed") return false;
  return ctx.startedAt > ctx.toIso(ctx.date, h.open);
}

/** The day's hours in minutes from its midnight (close past 1440 when it closes after midnight), or null when closed. */
export function dayMinutes(ctx: Pick<DayContext, "date" | "openAt" | "closeAt"> & { hours?: WeeklyHours }): { open: number; close: number } | null {
  const h = hoursOn(ctx, ctx.date);
  if (h === "closed") return null;
  const m = spanMinutes(h);
  return { open: m.openMin, close: m.closeMin };
}

/** A rule's hours between checks, never looser than the law's own (an "hours" cadence in the place's data). Undefined stays undefined. */
export function lawInterval(law: CheckLaw | undefined, ruleHours: number | undefined): number | undefined {
  const legal = law?.status === "law" && law.value.every.kind === "hours" ? law.value.every.hours : undefined;
  if (legal === undefined) return ruleHours;
  return ruleHours === undefined ? legal : Math.min(ruleHours, legal);
}
/** A rule's days between checks, never looser than the law's own (a "days" cadence in the place's data). Undefined stays undefined. */
export function lawDays(law: CheckLaw | undefined, ruleDays: number | undefined): number | undefined {
  const legal = law?.status === "law" && law.value.every.kind === "days" ? law.value.every.days : undefined;
  if (legal === undefined) return ruleDays;
  return ruleDays === undefined ? legal : Math.min(ruleDays, legal);
}

/** Past its grace time and not a first-day check. Every "Was due", shortfall and missed-check finding goes through this. */
export function isLate(e: Pick<ExpectedCheck, "lateAt" | "firstDay">, nowIso: IsoDateTime): boolean {
  return !e.firstDay && nowIso > e.lateAt;
}

export function expectedChecks(ctx: DayContext): ExpectedCheck[] {
  const out: ExpectedCheck[] = [];
  // A cooling batch runs on its own clock, open or closed day: food cooling on a Sunday still has to hit 70°F by 2 hours.
  // Every batch is timed, even where cooling isn't a base check: a running batch is never hidden (core cooling.ts).
  const rule = BASE_CHECKS.find((r) => r.trigger === "batch")!;
  for (const b of (ctx.cooling ?? []).filter((r) => !r.correction).map(coolingBatch)) {
    for (const c of coolingChecks(b, ctx.timeLabel)) out.push({ key: c.key, label: c.label, templateId: rule.templateId, dueAt: c.dueAt, lateAt: c.lateAt, blocking: false, required: true, hint: c.hint, requirementIds: rule.requirementIds, cooling: c.cooling });
  }
  // Rechecks after a fix (core whatidid.ts W4): a person's own clock, like a cooling batch, open or closed day.
  out.push(...recheckChecks(ctx));
  // Closed today (weekly hours): nothing else is expected, so nothing is due, late, missed or a first-day check.
  const day = dayMinutes(ctx);
  if (!day) return out.sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  const { open, close } = day;
  const level = ctx.level ?? "standard";
  const firstDay = beforeFirstFullDay(ctx);
  // Where the operating day starts: midnight, or when last night's overnight shift closed (its closing logs are last night's).
  const prev = hoursOn(ctx, addLocalDays(ctx.date, -1));
  const dayStart = ctx.toIso(ctx.date, hhmm(prev !== "closed" && isOvernight(prev) ? Math.min(toMinutes(prev.close), open) : 0));
  const push = (key: string, label: string, templateId: string, at: number, grace: number, blocking: boolean, req: string[], taskId?: Id, required = false, match?: Record<string, string>, opening = false) => {
    // Past midnight on an overnight day lands on the next date (hours.ts H3).
    const dueAt = isoAtMinute(ctx.date, at, ctx.toIso);
    out.push({ key: `${key}@${hhmm(at % 1440)}`, label, templateId, taskId, dueAt, lateAt: addMin(dueAt, grace), blocking, required, requirementIds: req, ...(match ? { match } : {}), ...(firstDay ? { firstDay: true as const } : {}), ...(opening && dayStart < dueAt ? { windowFrom: dayStart } : {}) });
  };
  // What the law at this place says about each built-in check, for this permit (ruleshapes.ts checkLawsAt; the state, then the
  // place's health authority). A check the law names runs at every level, at the law's cadence or tighter.
  const laws = checkLawsAt({ state: ctx.state, county: ctx.county }, ctx.permit);
  for (const r of BASE_CHECKS) {
    if (r.trigger === "batch") continue; // timed per batch, above
    if (r.permits && !r.permits.includes(ctx.permit)) continue;
    const basis = checkBasis(r.key, laws[r.key]);
    const legal = basis.required;
    // A law-only rule (the route sheet) exists only where the law here names it; elsewhere it is nobody's reminder.
    if (r.lawOnly && !legal) continue;
    if (!legal && LEVEL_RANK[level] < LEVEL_RANK[r.level]) continue;
    const text = r.templateId === "sanitizer" ? sanitizerCheckText(ctx.sanitizer, r.trigger === "shift_open" ? "open" : "recheck") : undefined;
    const label = text?.label ?? r.label;
    const before = out.length;
    if (r.trigger === "shift_open") push(r.key, label, r.templateId, open, r.graceMinutes, r.blocking, r.requirementIds, undefined, legal, undefined, true);
    if (r.trigger === "shift_close") push(r.key, label, r.templateId, close, r.graceMinutes, r.blocking, r.requirementIds, undefined, legal);
    // The place's own timing, else the level's ("thorough" tightens it); the law's own hours cap either (never loosened).
    const timing = timingOf(r, level, ctx.cadence);
    const every = lawInterval(laws[r.key], timing.everyHours);
    if (r.trigger === "every_hours" && every) for (let m = open + every * 60; m < close; m += every * 60) push(r.key, label, r.templateId, m, r.graceMinutes, false, r.requirementIds, undefined, legal);
    // Every N days: due from opening, all day, until a Record satisfies it; it stays listed (done) on the day it was.
    // At Thorough a rule that says so runs at each opening instead (the thermometer check), never blocking.
    if (r.trigger === "every_days" && r.everyDays) {
      if (timing.atOpen) push(r.key, label, r.templateId, open, 30, false, r.requirementIds, undefined, legal, undefined, true);
      else {
        const last = ctx.lastChecks?.[r.key];
        // The law's days are never loosened either (SNHD's route sheet: 30).
        const days = lawDays(laws[r.key], timing.everyDays ?? r.everyDays)!;
        if (!last || last >= ctx.date || Date.parse(ctx.date) - Date.parse(last) >= days * 86400000) push(r.key, label, r.templateId, open, Math.max(r.graceMinutes, close - open), false, r.requirementIds, undefined, legal, r.match);
      }
    }
    for (let i = before; i < out.length; i++) { if (legal) out[i]!.law = basis.line; else out[i]!.why = basis.line; }
    if (r.hint) for (let i = before; i < out.length; i++) out[i]!.hint = r.hint;
    if (text) for (let i = before; i < out.length; i++) out[i]!.hint = text.hint;
    const how = howToFor(r.templateId, { sanitizer: ctx.sanitizer, recheck: r.trigger === "every_hours" });
    if (how) for (let i = before; i < out.length; i++) out[i]!.howTo = how;
  }
  // A reading that looked wrong today (a failing temperature record): check the thermometer in ice water before the next one
  // (Food Code 4-502.11(B): calibrate "as necessary to ensure their accuracy"). Due when that reading was saved; never a wall.
  const doubt = ctx.entries.filter((r) => counts(r) && !r.evaluation.ok && DOUBT_TEMPLATES.has(r.templateId) && r.createdAt >= dayStart).sort((x, y) => x.createdAt.localeCompare(y.createdAt))[0];
  if (doubt) {
    const m = Math.round((Date.parse(doubt.createdAt) - Date.parse(ctx.toIso(ctx.date, "00:00"))) / 60000);
    out.push({ key: `${CALIBRATE_DOUBT_KEY}@${hhmm(((m % 1440) + 1440) % 1440)}`, label: "Check the thermometer in ice water", templateId: "calibration", dueAt: doubt.createdAt, lateAt: addMin(doubt.createdAt, 60), blocking: false, required: false, hint: CALIBRATE_DOUBT_HINT, why: "Recommended. A thermometer that reads wrong makes every log wrong; one ice-water check settles it.", requirementIds: ["fc-thermometers"], ...(firstDay ? { firstDay: true as const } : {}) });
  }
  for (const t of ctx.tasks) {
    if (t.active === false) continue;
    // The old "Pest walk-through" checklist item is now the built-in weekly walk-through (pest.ts): never twice.
    if (isLegacyPestWalkthrough(t)) continue;
    // A task for one dish (the buffet sign) only while that dish is on the menu (A14).
    if (ctx.menuItemIds && !taskFitsMenu(t, ctx.menuItemIds)) continue;
    const before = out.length;
    if (t.schedule.perShift) push(`task-${t.id}`, t.title, "task_done", t.category === "closing" ? close : open, 60, false, t.requirementIds, t.id, false, undefined, t.category !== "closing");
    else if (t.schedule.everyDays) {
      const last = ctx.lastDone[t.id];
      // Never done: due from its first due day (schedule.firstDue: a yearly renewal or a contractor's visit seeded at setup waits
      // for its own date, so a new place's day 1 stays light; tasks.ts starterItem), else from day one. Done: N days after.
      const due = last ? Date.parse(ctx.date) - Date.parse(last) >= t.schedule.everyDays * 86400000 : !t.schedule.firstDue || ctx.date >= t.schedule.firstDue;
      if (due) push(`task-${t.id}`, t.title, "task_done", open, close - open, false, t.requirementIds, t.id);
    }
    // The item's how travels to the row and its card: the owner's own words first, else the starter seed's. Never "" (no hint).
    const how = t.how?.trim() || seedForTask(t)?.how;
    if (how) for (let i = before; i < out.length; i++) out[i]!.hint = how;
  }
  return out.sort((a, b) => a.dueAt.localeCompare(b.dueAt));
}

/**
 * A Record satisfies an expected Check if it's the same template (and task) and was made in its window: from an hour
 * before it's due, or for an opening check from the start of the day (ExpectedCheck.windowFrom).
 */
/** Rechecks due after "What I did" (core whatidid.ts W4), from today's records and the open ones of earlier days. */
export function recheckChecks(ctx: Pick<DayContext, "entries" | "rechecks">): ExpectedCheck[] {
  const seen = new Set<string>();
  const out: ExpectedCheck[] = [];
  for (const r of [...(ctx.rechecks ?? []), ...ctx.entries]) {
    const f = r.fix;
    if (!f || f.status !== "recheck" || !f.recheckAt || seen.has(r.id)) continue;
    seen.add(r.id);
    const t = getTemplate(r.templateId);
    const after = f.steps[f.steps.length - 1]?.at ?? r.createdAt;
    out.push({ key: recheckKey(r.id), label: `Recheck: ${f.what}`, templateId: r.templateId, dueAt: f.recheckAt, lateAt: addMin(f.recheckAt, 15), blocking: false, required: false,
      hint: "Check it again and save the reading.", requirementIds: t?.requirementIds ?? [], ...(f.recheckMatch && Object.keys(f.recheckMatch).length ? { match: f.recheckMatch } : {}), recheck: { entryId: r.id, after } });
  }
  return out;
}

export function isSatisfied(e: ExpectedCheck, entries: DayContext["entries"], windowBeforeMin = 60): boolean {
  return !!satisfiedBy(e, entries, windowBeforeMin);
}

/** The record that satisfies an expected check (the latest one), so Today can offer "Not done after all" on it. */
export function satisfiedBy<R extends DayContext["entries"][number]>(e: ExpectedCheck, entries: readonly R[], windowBeforeMin = 60): R | undefined {
  const before = addMin(e.dueAt, -windowBeforeMin);
  const from = e.windowFrom && e.windowFrom < before ? e.windowFrom : before;
  let best: R | undefined;
  // A recheck: a reading of the same thing after the fix (never the record it rechecks).
  if (e.recheck) {
    for (const r of entries) if (counts(r) && r.id !== e.recheck.entryId && r.templateId === e.templateId && r.createdAt > e.recheck.after && (!e.match || Object.entries(e.match).every(([k, v]) => String(r.values?.[k] ?? "").toLowerCase() === v.toLowerCase())) && (!best || r.createdAt > best.createdAt)) best = r;
    return best;
  }
  // A cooling batch's check: only its own record answers it, whatever time it was made (core cooling.ts).
  if (e.cooling) {
    const own = entries.find((r) => r.id === e.cooling!.entryId);
    return own && counts(own) && coolingCheckDone(e.cooling.stage, own) ? own : undefined;
  }
  for (const r of entries) {
    if (counts(r) && r.templateId === e.templateId && (!e.taskId || r.taskId === e.taskId) && (!e.match || Object.entries(e.match).every(([k, v]) => r.values?.[k] === v)) && r.createdAt >= from && r.createdAt <= addMin(e.lateAt, 24 * 60) && (!best || r.createdAt > best.createdAt)) best = r;
  }
  return best;
}

export type FindingKind = "missed_check" | "failed_check" | "incident" | "review" | "not_checked_in" | "not_cleared" | "credential";
export interface Finding {
  kind: FindingKind;
  severity: "urgent" | "high" | "normal";
  title: string;
  detail: string;
  ref?: string;
  requirementIds: string[];
  /** What you can do about it, right now (actions.ts). */
  actions: QuickAction[];
  /** failed_check: the failed rule ids (templates.ts), so Nag can tell a safety problem from a routine one (nag.ts). */
  ruleIds?: string[];
  /** incident: what kind of report it is and when it was reported, so its card says what to do next (openreport.ts). */
  incidentType?: import("./model.ts").IncidentType;
  reportedAt?: string;
}

/** "Pastry case 45°F" style summary of a failed record, from its template columns. */
function failedSummary(r: DayContext["entries"][number], tpl: ReturnType<typeof getTemplate>): string {
  if (!tpl) return r.templateId;
  const bits = tpl.columns.filter((c) => ["text", "select", "temp_f", "ppm", "number"].includes(c.type) && c.key !== "action" && r.values[c.key] != null && r.values[c.key] !== "")
    .slice(0, 3).map((c) => (c.type === "temp_f" ? `${r.values[c.key]}°F` : c.type === "ppm" ? `${r.values[c.key]} ppm` : String(r.values[c.key])));
  return bits.length ? `${tpl.title.split(/[:,(]/)[0]!.trim()} · ${bits.join(" ")}` : tpl.title;
}

export function findings(ctx: DayContext): Finding[] {
  const out: Finding[] = [];
  const at = ctx.timeLabel ?? ((iso: IsoDateTime) => iso.slice(11, 16));
  for (const e of expectedChecks(ctx)) {
    if (isLate(e, ctx.now) && !isSatisfied(e, ctx.entries)) out.push({ kind: "missed_check", severity: e.blocking ? "urgent" : "high", title: `Still needed: ${e.label}`, detail: `Due ${at(e.dueAt)}${e.hint ? `. ${e.hint}` : ""}`, ref: e.key, requirementIds: e.requirementIds,
      actions: [{ kind: "do", label: "Log it now", target: { type: "check", id: e.key } }, { kind: "remind", label: "Remind whoever's on shift", target: { type: "check", id: e.key }, detail: `${e.label} is late.` }] });
  }
  for (const r of ctx.entries) {
    // Needs a fix: nothing written yet, or only what was asked ("Asked to: … Not done yet.", core whatidid.ts W2).
    if (counts(r) && !r.evaluation.ok && fixStatusOf(r) === "open") {
      const tpl = getTemplate(r.templateId);
      out.push({ kind: "failed_check", severity: "urgent", title: `Needs a fix: ${failedSummary(r, tpl)}`, detail: r.evaluation.violations.map((v) => v.message).join(" "), ref: r.id, requirementIds: tpl?.requirementIds ?? [], ruleIds: r.evaluation.violations.map((v) => v.ruleId),
        actions: [{ kind: "fix", label: "Add what was done", target: { type: "entry", id: r.id } }, ...supplyActions(r.evaluation.violations.map((v) => v.ruleId), ctx.supplies)] });
    }
  }
  for (const i of ctx.incidents) if (i.status !== "resolved") out.push({ kind: "incident", severity: i.severity === "urgent" ? "urgent" : i.severity === "high" ? "high" : "normal", title: i.title, detail: `Reported ${at(i.reportedAt)}`, ref: i.id, requirementIds: i.requirementIds, incidentType: i.type, reportedAt: i.reportedAt,
    actions: [{ kind: "open", label: "Open the report", target: { type: "incident", id: i.id } }] });
  const pending = ctx.reviews.filter((r) => r.status === "pending").length;
  if (pending) out.push({ kind: "review", severity: "normal", title: `${pending} scanned cell${pending > 1 ? "s" : ""} to confirm`, detail: "Photos that were hard to read or disagree with what's stored.", requirementIds: [], actions: [{ kind: "open", label: "Review them", target: { type: "review", id: "pending" } }] });
  // Closed today (weekly hours): nobody is asked for a check-in. The shift gate itself still holds for anyone who works.
  const closedToday = !dayMinutes(ctx);
  for (const p of ctx.onShift) {
    const gate = shiftGate(ctx, p.id);
    const first = firstName(p.name);
    for (const b of gate.blocking) {
      if (closedToday && b.kind === "not_checked_in") continue;
      const msg = b.kind === "not_checked_in" ? `Hi ${first}, please do your CleanCleaver check-in before you start. It takes 30 seconds.` : `Hi ${first}, please finish your CleanCleaver onboarding before your next food shift.`;
      // Manager-facing wording states the need, not the person's failure (PEOPLE_AND_NEGATION: no blame-shaped wording).
      const need = b.title.startsWith("hasn't done") ? "check-in still needed" : b.title.startsWith("hasn't finished") ? "lessons and sign-offs still needed" : b.title;
      out.push({ kind: b.kind, severity: "urgent", title: `${p.name}: ${need}`, detail: b.detail, ref: p.id, requirementIds: b.requirementIds,
        actions: [...personActions(p, msg), ...(b.kind === "not_cleared" ? [{ kind: "open" as const, label: "Open onboarding", target: { type: "employee" as const, id: p.id } }] : [])] });
    }
    const st = onboardingStatus(p.onboarding);
    if (st.permitExpiresInDays !== null && st.permitExpiresInDays >= 0 && st.permitExpiresInDays <= 30) out.push({ kind: "credential", severity: "normal", title: `${p.name}: food handler permit expires in ${st.permitExpiresInDays} days`, detail: "Renew before it lapses.", ref: p.id, requirementIds: ["ut-food-handler-permit"], actions: personActions(p, `Hi ${first}, your food handler permit expires in ${st.permitExpiresInDays} days. Please renew it and upload the new permit.`) });
    // SNHD (rule shapes: before_first_work): no card on file is a flag for the manager, never a block (ruleshapes.ts P1).
    else if (st.cardRule.beforeFirstWork && st.cardRule.line) out.push({ kind: "credential", severity: "high", title: `${p.name}: ${st.cardRule.line.toLowerCase()}`, detail: `Due before food work starts (${st.cardRule.citation}). Their check-in and safety work go on meanwhile.`, ref: p.id, requirementIds: ["food-handler-staff"], actions: [...personActions(p, `Hi ${first}, please get your food handler card before your next shift and add a photo of it in CleanCleaver.`), { kind: "open" as const, label: "Open their checklist", target: { type: "employee" as const, id: p.id } }] });
    // A course certificate on file (fix/cert-scan): the reminder names the permit-by day; a permit on file replaces it (st.course is null then).
    else if (st.course?.permitBy && st.course.daysLeft !== null && st.course.daysLeft >= 0 && st.course.daysLeft <= COURSE_REMIND_DAYS) out.push({ kind: "credential", severity: "high", title: `${p.name}: course done, permit due in ${st.course.daysLeft} day${st.course.daysLeft === 1 ? "" : "s"}`, detail: courseLine(st.course, ctx.date), ref: p.id, requirementIds: ["ut-food-handler-permit"], actions: [...personActions(p, `Hi ${first}, your food handler course is done. Please get your permit by ${certDay(st.course.permitBy, ctx.date)} and add a photo of it in CleanCleaver.`), { kind: "open" as const, label: "Open their checklist", target: { type: "employee" as const, id: p.id } }] });
    else if (st.cardRule.required && !st.cardRule.beforeFirstWork && st.clearance !== "public_food_work" && st.permitDueInDays >= 0 && st.permitDueInDays <= 7) out.push({ kind: "credential", severity: "high", title: `${p.name}: food handler permit due in ${st.permitDueInDays} days`, detail: words(ctx.state).permitDueDetail ?? "Their food handler permit is due soon.", ref: p.id, requirementIds: ["ut-food-handler-permit"], actions: personActions(p, `Hi ${first}, your food handler permit is due in ${st.permitDueInDays} days.${words(ctx.state).courseListUrl ? ` Here's the course list: ${words(ctx.state).courseListUrl}` : ""}`) });
  }
  // The person in charge must be a certified food protection manager where the authority says so (SNHD 2-102.2): a gentle
  // note for managers with the way to add the certificate. Never a block (ruleshapes.ts P1).
  const pic = ctx.onShift.find((p) => p.isPersonInCharge);
  if (pic && !closedToday) {
    const note = picCertNote(shapesAt({ state: ctx.state, county: ctx.county }), { picName: pic.name, picCreds: pic.onboarding.credentials, today: ctx.date, permit: ctx.permit });
    if (note) out.push({ kind: "credential", severity: "normal", title: note.title, detail: `${note.detail} (${note.citation})`, ref: pic.id, requirementIds: ["cfsm"],
      actions: [{ kind: "open", label: note.action, target: { type: "employee", id: pic.id } }] });
  }
  const rank = { urgent: 0, high: 1, normal: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

/** Minimum clearance to handle food at this kind of location. */
export function requiredClearance(permit: PermitType): Clearance {
  return permit === "private_members_only" ? "members_only_events" : "public_food_work";
}

/**
 * The one hard block (PRINCIPLES.md S4): a person can't start food work until they've checked in healthy today
 * and are cleared for this kind of location.
 */
export function shiftGate(ctx: DayContext, employeeId: Id): { ready: boolean; blocking: { kind: FindingKind; title: string; detail: string; requirementIds: string[] }[] } {
  const blocking: { kind: FindingKind; title: string; detail: string; requirementIds: string[] }[] = [];
  const ci = checkinFor(ctx, employeeId);
  const p = ctx.onShift.find((x) => x.id === employeeId);
  // "I'm sick or was exposed", sent today and still open: no food work until a check-in made after the report says OK
  // (Food Code 2-201.11 reporting, 2-201.12 exclude or restrict: the check-in decides which, as everywhere else). The
  // action only, never the symptom (T4). The person in charge resolving the report also lifts it: they decided.
  const sick = sickReportToday(ctx, p?.onboarding.employee.userId);
  // The person in charge's decision (Food Code 2-201.12, 2-201.13): "Excluded until ..." or "Restricted" holds whatever a
  // later check-in says, so an exclusion doesn't end when the answers turn to No. "Cleared to return" lifts the check-in
  // and sick-report stops made before it (a doctor's note can clear someone whose honest answers still say yes).
  const dec = latestDecision(ctx.healthDecisions, employeeId);
  const decidedAt = dec?.at ?? "";
  if (dec && decisionInEffect(dec, ctx.date)) blocking.push({ kind: "not_checked_in", title: HEALTH_DECISION_BLOCK_TITLE, detail: `${healthDecisionLine(dec)} (Food Code 2-201.12, 2-201.13).`, requirementIds: ["fc-employee-health"] });
  else if (sick && !(ci?.at && ci.at > sick.reportedAt) && !(!!dec && decidedAt >= sick.reportedAt)) blocking.push({ kind: "not_checked_in", title: SICK_REPORT_BLOCK_TITLE, detail: "No food work until a new check-in says OK (Food Code 2-201.12).", requirementIds: ["fc-employee-health"] });
  else if (!ci) blocking.push({ kind: "not_checked_in", title: "hasn't done today's check-in", detail: "Health questions and handwashing, 30 seconds on their phone.", requirementIds: ["fc-employee-health", "fc-handwashing"] });
  else if (!ci.okToWork && !(dec?.decision === "cleared" && ci.at !== undefined && decidedAt >= ci.at)) {
    // The action, never the symptom (minimum necessary disclosure).
    const a = ci.health ? checkinAction(ci.health) : "exclude";
    // The place's lines (rule shapes: Nevada's person in charge also sees who to tell and how fast).
    const act = checkinActionsFor(ctx.state, ctx.county)[a === "ok" || a === "caution" ? "cover" : a];
    blocking.push({ kind: "not_checked_in", title: a === "cover" ? "needs a bandage and glove before food work" : "no food work today", detail: `${act.personInCharge} (${act.citation})`, requirementIds: ["fc-employee-health"] });
  }
  if (p) {
    const st = onboardingStatus(p.onboarding);
    const need = requiredClearance(ctx.permit);
    const rank = { not_cleared: 0, members_only_events: 1, public_food_work: 2 };
    // Company policy (reaffirm training): lessons and signed acknowledgements before the first food shift.
    if (!st.foodWorkReady) blocking.push({ kind: "not_cleared", title: "hasn't finished the lessons and acknowledgements", detail: lessonsGateDetail(st.track), requirementIds: ["fc-employee-health", "fc-handwashing", "fc-bare-hand"] });
    // Utah allows food work before the permit only within 30 days of starting (Utah Code 26B-7-413).
    // Only where the rule shape's policy says a late card stops food work (Utah's day 30); SNHD's before-first-shift card flags only.
    else if (st.cardRule.blocksFoodWorkWhenLate && rank[st.clearance] < rank[need] && st.permitDueInDays < 0) blocking.push({ kind: "not_cleared", title: "past the 30-day window without a verified food handler permit", detail: `Missing: ${st.missing.join(", ")}`, requirementIds: ["ut-food-handler-permit"] });
  }
  return { ready: blocking.length === 0, blocking };
}

/** shiftGate's words for someone who reported themself sick today (manager and worker screens match on them). */
export const SICK_REPORT_BLOCK_TITLE = "shouldn't do food work today";

/** An open sick report sent today (this location's day) by this sign-in, if any. */
export function sickReportToday(ctx: Pick<DayContext, "incidents" | "date" | "toIso">, userId: Id | undefined): DayContext["incidents"][number] | undefined {
  if (!userId) return undefined;
  const dayStart = ctx.toIso(ctx.date, "00:00");
  return ctx.incidents.filter((i) => i.type === "illness" && i.reportedBy === userId && i.status !== "resolved" && i.reportedAt >= dayStart)
    .sort((a, b) => b.reportedAt.localeCompare(a.reportedAt))[0];
}

/** Is the location ready to serve food? All blocking opening Checks done. */
export function openForFood(ctx: DayContext): { ready: boolean; missing: ExpectedCheck[] } {
  const missing = expectedChecks(ctx).filter((e) => e.blocking && !isSatisfied(e, ctx.entries));
  return { ready: missing.length === 0, missing };
}

/** Share of checks due so far that have a Record: the north-star number (PRINCIPLES.md A5). */
export function completion(ctx: DayContext): { due: number; done: number; rate: number } {
  // First-day checks are a start, not a shortfall: they count once they're done, never against the day.
  const due = expectedChecks(ctx).filter((e) => (e.firstDay ? isSatisfied(e, ctx.entries) : e.lateAt <= ctx.now));
  const done = due.filter((e) => isSatisfied(e, ctx.entries)).length;
  return { due: due.length, done, rate: due.length ? done / due.length : 1 };
}

export type Audience = "on_shift" | "manager" | "owner";
export interface Notification {
  /** Stable id: the sender records it so a notification is never sent twice. */
  key: string;
  at: IsoDateTime;
  kind: "reminder" | "checkin" | "shortfall" | "escalation" | "failed" | "summary";
  audience: Audience[];
  employeeId?: Id;
  title: string;
  body: string;
}

/**
 * What to send between `from` (exclusive) and ctx.now (inclusive). Run it every few minutes; dedupe on `key`.
 * The pattern for forgetful people: a nudge when it's due, a shortfall alert when it's late, and the owner hears
 * only when something the law requires is still missing an hour later.
 */
export function notificationsDue(ctx: DayContext, from: IsoDateTime): Notification[] {
  const out: Notification[] = [];
  const inWindow = (at: IsoDateTime) => at > from && at <= ctx.now;
  const clock = ctx.timeLabel ?? ((iso: IsoDateTime) => iso.slice(11, 16));
  for (const e of expectedChecks(ctx)) {
    const done = isSatisfied(e, ctx.entries);
    if (done) continue;
    // A cooling batch's own heads-up and deadline notices (below) replace the generic reminder; late still tells the
    // manager, and a missed cooling deadline is a required log.
    if (e.cooling) {
      if (inWindow(e.lateAt)) out.push({ key: `short:${e.key}`, at: e.lateAt, kind: "shortfall", audience: ["on_shift", "manager"], title: `Still needed: ${e.label}`, body: "The cooling deadline has passed. Read it now: over the limit, it gets thrown out." });
      continue;
    }
    // Before the first full day: no nudge for a time before the place existed, and never a shortfall or escalation.
    if (e.firstDay) { if (inWindow(e.dueAt) && (!ctx.startedAt || e.dueAt >= ctx.startedAt)) out.push({ key: `remind:${ctx.date}:${e.key}`, at: e.dueAt, kind: "reminder", audience: ["on_shift"], title: `Time to: ${e.label}`, body: "Takes about a minute. Tap to log it." }); continue; }
    if (inWindow(e.dueAt)) out.push({ key: `remind:${ctx.date}:${e.key}`, at: e.dueAt, kind: "reminder", audience: ["on_shift"], title: `Time to: ${e.label}`, body: "Takes about a minute. Tap to log it." });
    if (inWindow(e.lateAt)) out.push({ key: `short:${ctx.date}:${e.key}`, at: e.lateAt, kind: "shortfall", audience: ["on_shift", "manager"], title: `Still needed: ${e.label}`, body: `It was due at ${clock(e.dueAt)}.` });
    // An hour late, the owner hears: a record the law names (its own words say which law), or an opening check "Open for food"
    // is waiting on (the app's gate, never called required: docs/compliance/CHECKS-BY-LAW.md).
    const esc = addMin(e.lateAt, 60);
    if (e.required && inWindow(esc)) out.push({ key: `esc:${ctx.date}:${e.key}`, at: esc, kind: "escalation", audience: ["manager", "owner"], title: `Missing a record the law asks for: ${e.label}`, body: `${e.law ? `${e.law} ` : ""}Log it now, or write down why it was missed.` });
    else if (e.blocking && inWindow(esc)) out.push({ key: `esc:${ctx.date}:${e.key}`, at: esc, kind: "escalation", audience: ["manager", "owner"], title: `Still not done: ${e.label}`, body: "Open for food is waiting on it. Log it now, or write down why it was missed." });
  }
  const day = dayMinutes(ctx);
  const openIso = day ? isoAtMinute(ctx.date, day.open, ctx.toIso) : undefined;
  for (const p of openIso ? ctx.onShift : []) {
    if (checkinFor(ctx, p.id)) continue;
    const at = addMin(openIso!, -15);
    if (inWindow(at)) out.push({ key: `checkin:${ctx.date}:${p.id}`, at, kind: "checkin", audience: ["on_shift"], employeeId: p.id, title: "Check in before your shift", body: "A few quick health questions, then wash your hands. About 30 seconds." });
  }
  for (const r of ctx.entries) {
    if (!r.evaluation.ok && !r.correctiveAction && inWindow(r.createdAt)) out.push({ key: `failed:${r.id}`, at: r.createdAt, kind: "failed", audience: ["on_shift", "manager"], title: `Out of range: ${getTemplate(r.templateId)?.title ?? r.templateId}`, body: r.evaluation.violations.map((v) => v.message).join(" ") + " Say what you did about it." });
  }
  // Cooling timers (core cooling.ts): 1:45, 2:00, 5:45, 6:00 for each open batch. The phone schedules the same notices
  // locally; this is the seam a push sender reads (keys dedupe).
  for (const b of (ctx.cooling ?? []).filter((r) => !r.correction).map(coolingBatch).filter(isOpenBatch)) {
    for (const n of coolingNotices(b)) if (inWindow(n.at)) out.push({ key: n.key, at: n.at, kind: "reminder", audience: ["on_shift"], title: n.title, body: n.body });
  }
  // No end-of-day summary on a closed day: there was no day.
  const summaryAt = day ? addMin(isoAtMinute(ctx.date, day.close, ctx.toIso), 30) : undefined;
  if (summaryAt && inWindow(summaryAt)) {
    const c = completion(ctx);
    out.push({ key: `summary:${ctx.date}`, at: summaryAt, kind: "summary", audience: ["owner"], title: `Today: ${c.done} of ${c.due} checks logged`, body: c.done === c.due ? "Every check logged. Nice." : "Open the app to see what was missed." });
  }
  return out.sort((a, b) => a.at.localeCompare(b.at));
}

/**
 * What a logging level runs at this place, and how many logs that is on an open day (the weekly pest walk-through not
 * counted): the one source for Setup's "What you'll do each day", Settings' level cards and Do's count, so they can't
 * disagree ("Essentials: about 7 a day" beside 22 checks; audit #14d). Checklist items the place set up come on top,
 * the same at every level.
 */
/** A built-in check as a place's plan lists it: the rule, whether the law here names it, and the basis line (checkBasis). */
export type PlannedCheck = CheckRule & CheckBasis;
export function levelPlan(p: { permit: PermitType; level: LogLevel; openAt: string; closeAt: string; cooks?: boolean; /** The place's rules (absent = Utah, no county). */ state?: UsState; county?: string | null; /** The place's own timing (Location.cadence). */ cadence?: CadenceOverrides }): { rules: PlannedCheck[]; perDay: number } {
  const laws = checkLawsAt({ state: p.state, county: p.county }, p.permit);
  // The cooling log is listed where food is cooked and cooled (it runs per batch, so it adds nothing to the per-day count).
  const rules = BASE_CHECKS.filter((r) => (!r.cooks || !!p.cooks) && (!r.permits || r.permits.includes(p.permit)))
    .map((r) => ({ ...r, ...checkBasis(r.key, laws[r.key]) }))
    .filter((r) => r.required || (!r.lawOnly && LEVEL_RANK[p.level] >= LEVEL_RANK[r.level]));
  const ctx: DayContext = { state: p.state, county: p.county, ...(p.cadence ? { cadence: p.cadence } : {}), date: "2026-01-05", now: "2026-01-05T00:00:00.000Z", permit: p.permit, level: p.level, openAt: p.openAt, closeAt: p.closeAt, toIso: (d, t) => `${d}T${t}:00.000Z`,
    tasks: [], lastDone: {}, lastChecks: { "pest-walkthrough": "2026-01-04", "open-calibrate": "2026-01-04" }, entries: [], checkins: [], onShift: [], incidents: [], reviews: [] };
  return { rules, perDay: expectedChecks(ctx).length };
}
/** "7 quick logs a day" (levelPlan), plus the place's own checklist items when it has some. */
export function levelLine(perDay: number, checklist = 0): string {
  const logs = `${perDay} quick log${perDay === 1 ? "" : "s"} a day`;
  return checklist ? `${logs}, plus your ${checklist} checklist item${checklist === 1 ? "" : "s"}: ${perDay + checklist} in all` : logs;
}
